/**
 * Live (word-by-word) Retell transcript, straight from Retell's monitor stream.
 *
 * v3 gateway calls deliver no transcript to the browser, and the retell-webhook
 * relay only fires at turn boundaries, so a sentence appears all at once after
 * it is finished. Retell's /v2/monitor-call WebSocket instead re-sends each
 * utterance as it grows, which is what the chat bubbles need. It accepts a
 * domain-allowlisted PUBLIC key (verified live 2026-09-26: a public key reached
 * `monitoring` and streamed growth for both roles at ~340ms), so no server-side
 * key goes near the browser.
 *
 * The call itself is still created server-side by the retell-web-call edge
 * function; this only watches it. If the stream never starts (key missing a
 * permission, domain not allowlisted, call not ongoing yet), `fallback` takes
 * over so bubbles degrade to the per-turn webhook relay instead of vanishing.
 */
export const subscribeRetellMonitorTranscript = (callId, onTurns, { publicKey, fallback = null } = {}) => {
  let session = null;
  let fallbackUnsub = null;
  let closed = false;
  let streamed = false;
  let fellBack = false;

  const toTurns = (transcript) => (Array.isArray(transcript) ? transcript : [])
    .filter((u) => (u?.role === 'agent' || u?.role === 'user') && typeof u.content === 'string')
    .map((u) => ({ role: u.role, content: u.content }));

  // Only before anything streamed: a drop mid-call is the SDK's to retry, and
  // switching sources then would rewind the bubbles to the last finished turn.
  const switchToRelay = (why) => {
    if (fellBack || streamed || closed || !fallback) return;
    fellBack = true;
    console.warn('[retellMonitor] falling back to the webhook relay:', why);
    fallbackUnsub = fallback(callId, onTurns);
  };

  (async () => {
    const { RetellClient } = await import('retell-client-js-sdk');
    if (closed) return;
    const client = new RetellClient({ key: publicKey });
    session = client.monitorCall({
      call_id: callId,
      hooks: {
        onTranscript: (transcript) => {
          streamed = true;
          onTurns(toTurns(transcript));
        },
        onError: (err) => switchToRelay(err?.message ?? String(err)),
      },
    });
  })().catch((e) => switchToRelay(e?.message ?? String(e)));

  return () => {
    closed = true;
    try { session?.disconnect?.(); } catch { /* already gone */ }
    session = null;
    fallbackUnsub?.();
    fallbackUnsub = null;
  };
};
